import { useNavigate } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppDateInput from '../../../components/AppDateInput'
import AppPagination from '../../../components/AppPagination'
import AppActionMenu from '../../../components/AppActionMenu'
import SharedFilters from '../../../components/SharedFilters'
import DateModifiedIcon from '../../../components/DateModifiedIcon'
import { useJobListScreen } from './hooks/useJobListScreen'
import { canAddTimesheet } from './jobHelpers'
import { AppTooltip } from '../../../components/AppTooltip'

function statusChipClass(status) {
  if (status === 'IN_PROGRESS') return 'bg-primary-fixed text-primary'
  if (status === 'PENDING_REVIEW') return 'bg-amber-100 text-amber-700'
  if (status === 'COMPLETED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'CLOSED') return 'bg-rose-100 text-rose-700'
  return 'bg-surface-container-high text-on-surface-variant'
}

function progressBarClass(status) {
  if (status === 'PENDING_REVIEW') return 'bg-amber-400'
  if (status === 'COMPLETED') return 'bg-emerald-500'
  if (status === 'CLOSED') return 'bg-rose-500'
  return 'bg-primary'
}

function overrunBadgeChip(flag) {
  if (flag === 'RED') {
    return (
      <AppTooltip content="Overrun: actual hours exceeded average by more than 20%">
        <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700">
          <span className="material-symbols-outlined text-[10px]">warning</span>
        </span>
      </AppTooltip>
    )
  }
  if (flag === 'GREEN') {
    return (
      <AppTooltip content="Efficient: actual hours were below the benchmark average">
        <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
          <span className="material-symbols-outlined text-[10px]">verified</span>
        </span>
      </AppTooltip>
    )
  }
  return null
}

function initials(name) {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export default function JobInventoryListScreen() {
  const { state, actions } = useJobListScreen()
  const navigate = useNavigate()

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Operations</span>
            <span className="material-symbols-outlined text-xs text-on-surface-variant">chevron_right</span>
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">Job Management</span>
          </div>
          <h1 className="text-5xl font-extrabold tracking-tight text-on-surface">Job Registry</h1>
        </div>
        {/* 
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="rounded-lg bg-secondary-container px-5 py-2.5 text-sm font-semibold text-on-secondary-container transition-colors hover:bg-primary-fixed"
          >
            Export Data
          </button>
          <Link
            to="/nerve/job/entry"
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-on-primary shadow-lg shadow-primary/10 transition-opacity hover:opacity-90"
          >
            Generate Report
          </Link>
        </div> */}
      </header>

      <div className="mb-6">
        <SharedFilters
          searchField={state.searchField}
          setSearchField={actions.setSearchField}
          searchFieldOptions={state.searchFieldOptions}
          searchText={state.searchText}
          setSearchText={actions.setSearchText}
          statusFilter={state.statusFilter}
          setStatusFilter={actions.setStatusFilter}
          statusOptions={state.statusOptions}
          projectFilter={state.projectFilter}
          setProjectFilter={actions.setProjectFilter}
          projectOptions={state.projectOptions}
          taskFilter={state.taskFilter}
          setTaskFilter={actions.setTaskFilter}
          taskOptions={state.taskOptions}
          agentFilter={state.responsibleFilter}
          setAgentFilter={actions.setResponsibleFilter}
          agentOptions={state.userOptions}
          companyFilter={state.companyFilter}
          setCompanyFilter={actions.setCompanyFilter}
          companyOptions={state.companyOptions}
          branchFilter={state.branchFilter}
          setBranchFilter={actions.setBranchFilter}
          branchOptions={state.branchOptions}
          departmentFilter={state.departmentFilter}
          setDepartmentFilter={actions.setDepartmentFilter}
          departmentOptions={state.departmentOptions}
          overrunFilter={state.overrunFilter}
          setOverrunFilter={actions.setOverrunFilter}
          overrunOptions={state.overrunOptions}
          fromDate={state.fromDate}
          setFromDate={actions.setFromDate}
          toDate={state.toDate}
          setToDate={actions.setToDate}
          onReset={actions.onReset}
          visibleAccordionFilters={[
            'project',
            'task',
            'company',
            'branch',
            'department',
            'overrun',
            'fromDate',
            'toDate',
          ]}
        />
      </div>

      {state.error ? (
        <div className="mb-6 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="overflow-hidden rounded-2xl bg-surface-container-low">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1160px] border-separate border-spacing-0 text-left">
            <thead>
              <tr className="bg-surface-container-highest/30">
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Job Code</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Job Name</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Project / Task</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">RP</th>
                <th className="px-9 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Status</th>
                <th className="px-4 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Timeline</th>
                <th className="px-6 py-5 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Utilization</th>
                <th className="px-6 py-5" />
              </tr>
            </thead>

            <tbody className="divide-y divide-outline-variant/10">
              {state.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading jobs...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    No jobs found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/nerve/job/entry?id=${row.id}`, { state: { returnTo: `/nerve/job/list${window.location.search}` } })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        navigate(`/nerve/job/entry?id=${row.id}`, { state: { returnTo: `/nerve/job/list${window.location.search}` } })
                        return
                      }
                    }}
                    className={`group cursor-pointer transition-colors hover:bg-surface-container-lowest focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50${row.overrunFlag === 'RED' ? ' border-l-4 border-rose-500 bg-rose-50/30' : ''}`}
                  >
                    <td className="px-6 py-6 font-mono text-sm font-bold text-primary">{row.jobCode}</td>

                    <td className="px-6 py-6">
                      <div className="flex items-center gap-1">

                        <span className="text-base font-semibold text-on-surface">{overrunBadgeChip(row.overrunFlag)}  {row.jobName}</span>
                      </div>
                      <div className="text-xs text-on-surface-variant">{row.taskName}</div>
                    </td>

                    <td className="px-6 py-6">
                      <div className="text-sm font-medium text-on-surface">{row.projectName}</div>
                      <div className="text-xs text-on-surface-variant">{row.taskName}</div>
                    </td>

                    <td className="px-6 py-6">
                      {row.assignedUser ? (
                        <div className="flex -space-x-2">
                          <span
                            className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-surface-container-low bg-tertiary text-[10px] font-bold text-on-tertiary"
                            title={row.assignedUser.name}
                          >
                            {initials(row.assignedUser.name)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-on-surface-variant">Unassigned</span>
                      )}
                    </td>

                    <td className="px-6 py-6 w-[170px]">
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${statusChipClass(row.status)}`}>
                        {row.statusLabel}
                      </span>
                      {/* {overrunBadgeChip(row.overrunFlag)} */}
                    </td>

                    <td className="px-4 py-4 text-sm font-medium text-on-surface-variant">
                      <div className="flex items-center gap-1.5">
                        <span>{row.timelineText}</span>
                        <DateModifiedIcon
                          hasDateChanges={row.hasDateChanges}
                          module="JOB"
                          entityId={row.id}
                        />
                      </div>
                    </td>

                    <td className="px-6 py-6 min-w-[170px]">
                      <div className="flex items-center gap-3">
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-variant">
                          <div
                            className={`h-full rounded-full ${progressBarClass(row.status)}`}
                            style={{ width: `${Math.min(100, Math.max(0, row.progress))}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-on-surface">{row.progress}%</span>
                      </div>
                      <div className="mt-1 text-[10px] font-medium text-on-surface-variant">
                        {row.loggedHours}/{row.plannedHours || 0} hrs
                      </div>

                    </td>

                    <td className="px-6 py-6 text-right" onClick={(event) => event.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="h-8 w-8 rounded-md bg-transparent p-0 text-on-surface-variant opacity-0 transition-all hover:bg-surface-container-highest hover:text-primary group-hover:opacity-100"
                        menuClassName="min-w-[176px]"
                        items={[
                          { key: 'edit_job', label: 'Edit Job', icon: 'edit' },
                          canAddTimesheet(row.status) && { key: 'add_timesheet', label: 'Add Timesheet', icon: 'post_add' },
                        ].filter(Boolean)}
                        onSelect={(key) => {
                          if (key === 'edit_job') {
                            navigate(`/nerve/job/entry?id=${row.id}`, { state: { returnTo: `/nerve/job/list${window.location.search}` } })
                            return
                          }
                          if (key === 'add_timesheet') {
                            navigate(`/nerve/timesheet/entry?jobId=${row.id}`)
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between bg-surface-container-highest/20 px-6 py-6">
          <span className="text-sm font-medium text-on-surface-variant">
            Showing <span className="text-on-surface">{state.rangeStart} - {state.rangeEnd}</span> of{' '}
            <span className="text-on-surface">{state.totalCount}</span> entries
          </span>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        <article className="relative overflow-hidden rounded-2xl bg-primary p-8 text-on-primary">
          <div className="relative z-10">
            <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-primary-fixed-dim">Workload Velocity</h4>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black">{state.stats.avgUtilization}%</span>
              <span className="text-sm font-medium text-primary-fixed-dim">live utilization</span>
            </div>
            <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-white" style={{ width: `${Math.max(6, state.stats.avgUtilization)}%` }} />
            </div>
          </div>
          <span className="material-symbols-outlined pointer-events-none absolute -bottom-8 -right-10 text-[160px] text-white/10">
            trending_up
          </span>
        </article>

        <article className="rounded-2xl bg-surface-container-low p-8">
          <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Active Ticket Linkage</h4>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-4xl font-black text-on-surface">{state.stats.inProgress}</span>
              <p className="mt-1 text-xs font-medium text-on-surface-variant">Jobs currently in progress</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-container">
              <span className="material-symbols-outlined text-primary">confirmation_number</span>
            </div>
          </div>
        </article>

        <article className="rounded-2xl bg-surface-container-low p-8">
          <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-on-surface-variant">Departmental Split</h4>
          <div className="flex h-12 items-end gap-1">
            <div className="h-[78%] flex-1 rounded-t-sm bg-primary" />
            <div className="h-[42%] flex-1 rounded-t-sm bg-primary/60" />
            <div className="h-[61%] flex-1 rounded-t-sm bg-primary/40" />
            <div className="h-[29%] flex-1 rounded-t-sm bg-primary/20" />
            <div className="h-[88%] flex-1 rounded-t-sm bg-primary/80" />
          </div>
          <div className="mt-2 flex justify-between">
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">Eng</span>
            <span className="text-[10px] font-bold uppercase text-on-surface-variant">R&D</span>
          </div>
        </article>
      </section>
    </div>
  )
}
